import { useEffect, useMemo, useState, useRef } from "react";
import { ActionIcon, Center, Flex, Loader, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";
import VerificationStatusBadge from "../AdminStausBadge";

type CompanyListItem = {
  id: string;
  legalName: string | null;
  gtid: string;
  status: string;
  lastSeenAt: string | null;
  creditBalance: number;
};

const PAGE_SIZE = 10;

const VERIFICATION_QUEUE_STATUSES = ["UNDER_REVIEW", "SYSTEM_VERIFIED"] as const;

export default function AdminPendingVerificationPage() {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const filtered = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(filtered.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams });

    if (paramsString === previousParamsRef.current) {
      return;
    }

    previousParamsRef.current = paramsString;

    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const filter: Record<string, unknown> = {
          statusIn: [...VERIFICATION_QUEUE_STATUSES],
        };
        if (filterParams.legalName) {
          filter.legalName = filterParams.legalName;
        }
        if (filterParams.gtid) {
          filter.gtid = filterParams.gtid;
        }

        const res = await trpc.admin.listAll.query({
          page: params.page,
          limit: params.limit,
          filter,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setCompanies(
          res.companies.map((c: any) => ({
            id: c.id || c.gtid,
            legalName: c.name,
            gtid: c.gtid,
            status: c.verificationStatus,
            lastSeenAt: c.lastSeenAt ?? null,
            creditBalance: c.creditBalance ?? 0,
          }))
        );
        setTotalRows(res.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setCompanies([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<CompanyListItem>[] = useMemo(
    () => [
      {
        accessorKey: "legalName",
        header: "Company Name",
        size: 220,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "gtid",
        header: "Reference ID",
        size: 150,
      },
      {
        accessorKey: "status",
        header: "Verification Status",
        size: 170,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return <VerificationStatusBadge status={value} />;
        },
      },
      {
        accessorKey: "lastSeenAt",
        id: "lastSeenAt",
        header: "Last Seen",
        size: 160,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          if (!value) return <span style={{ color: "#94a3b8" }}>Never</span>;
          const date = new Date(value);
          const now = new Date();
          const diffMs = now.getTime() - date.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMs / 3600000);
          const diffDays = Math.floor(diffMs / 86400000);

          let label: string;
          if (diffMins < 1) label = "Just now";
          else if (diffMins < 60) label = `${diffMins}m ago`;
          else if (diffHours < 24) label = `${diffHours}h ${diffMins % 60}m ago`;
          else if (diffDays < 7) label = `${diffDays}d ${diffHours % 24}h ago`;
          else label = date.toLocaleDateString();

          return (
            <Tooltip label={date.toLocaleString()}>
              <span style={{ color: diffMins < 5 ? "#16a34a" : "#64748b" }}>{label}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "creditBalance",
        header: "Credit Balance",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          const value = cell.getValue() as number;
          return <span style={{ fontWeight: 500 }}>{value}</span>;
        },
      },
    ],
    []
  );

  if (loading && companies.length === 0) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl" gap="md">
      <div>
        <Title order={2} fw={400}>
          Verification queue
        </Title>
        <Text size="sm" c="dimmed" mt={6}>
          Companies that submitted for verification (under review) or completed system verification and may need
          your approval.
        </Text>
      </div>

      <MantineReactTable
        columns={columns}
        data={companies}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Tooltip label="View Details">
            <ActionIcon
              c="#475569"
              variant="transparent"
              onClick={() => navigate(`/admin/companies/${row.original.id}`)}
            >
              <IconEye />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 220,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 280px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No companies in the verification queue.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
